import { get, patch } from "@libs:axios";
import { createQueryKeys } from "@lukemorales/query-key-factory";
import {
  useMutation,
  UseMutationOptions,
  useQuery,
  UseQueryOptions,
} from "@tanstack/react-query";
import { AxiosError } from "axios";
import { omit } from "lodash";
import { CamelCasedPropertiesDeep } from "type-fest";

import { ConversationGetSchema, ConversationRenameSchema } from "@:openapi";

export interface Conversation {
  GET_ALL: {
    Response: CamelCasedPropertiesDeep<Array<ConversationGetSchema>>;
  };
  PATCH: {
    RequestBody: CamelCasedPropertiesDeep<ConversationRenameSchema>;
    RequestPath: { id: string };
    Response: CamelCasedPropertiesDeep<ConversationGetSchema>;
  };
}

export const conversationQueryKey = createQueryKeys("conversation", {
  getAll: null,
});

export const useQueryGetAllConversations = (
  options?: Omit<
    UseQueryOptions<Conversation["GET_ALL"]["Response"], AxiosError>,
    "queryKey"
  >,
) => {
  return useQuery<Conversation["GET_ALL"]["Response"], AxiosError>({
    queryKey: conversationQueryKey.getAll.queryKey,
    queryFn: () => {
      return get<Conversation["GET_ALL"]["Response"]>("/api/conversations/");
    },
    ...options,
  });
};

export const useMutationRenameConversation = (
  options?: UseMutationOptions<
    Conversation["PATCH"]["Response"],
    AxiosError,
    Conversation["PATCH"]["RequestBody"] & Conversation["PATCH"]["RequestPath"]
  >,
) => {
  return useMutation({
    mutationFn: (
      data: Conversation["PATCH"]["RequestBody"] &
        Conversation["PATCH"]["RequestPath"],
    ) => {
      return patch<Conversation["PATCH"]["Response"]>(
        `/api/conversations/${data.id}`,
        omit(data, ["id"]),
      );
    },
    ...options,
  });
};
